import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, User, Clock, MessageSquare, Edit, Trash2, Send, EyeOff, Eye } from 'lucide-react';
import apiClient from '../api/client';
import { useLanguage } from '../contexts/LanguageContext';

const PostDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [post, setPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const loadPost = useCallback(async () => {
    try { setPost(await apiClient.getPost(postId)); } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [postId]);

  const loadReplies = useCallback(async () => {
    try { setReplies(await apiClient.getReplies(postId)); } catch (e) { console.error(e); }
  }, [postId]);

  const loadCurrentUser = useCallback(async () => {
    try { setCurrentUser(await apiClient.getCurrentUser()); } catch { setCurrentUser(null); }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadPost();
    loadReplies();
    loadCurrentUser();
  }, [loadPost, loadReplies, loadCurrentUser]);

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim() || !currentUser) return;
    setSubmitting(true);
    try { await apiClient.createReply(postId, replyContent); setReplyContent(''); loadReplies(); loadPost(); }
    catch (e) { console.error(e); alert(t('postDetail.replyFailed')); }
    finally { setSubmitting(false); }
  };

  const handleDeletePost = async () => {
    if (!window.confirm(t('postDetail.deleteConfirm'))) return;
    try { await apiClient.deletePost(postId); navigate('/forum'); }
    catch (e) { console.error(e); alert(t('postDetail.deleteFailed')); }
  };

  const formatDate = (ds) => { if (!ds) return ''; return new Date(ds).toLocaleString(language === 'zh' ? 'zh-CN' : 'en-US', { year:'numeric',month:'long',day:'numeric',hour:'2-digit',minute:'2-digit' }); };
  const formatRelativeTime = (ds) => {
    if (!ds) return '';
    const d = new Date(ds), n = new Date(), ms = n - d;
    const m = Math.floor(ms/60000), h = Math.floor(ms/3600000), dd = Math.floor(ms/86400000);
    if (m < 60) return `${m}${t('forum.minutesAgo')}`;
    if (h < 24) return `${h}${t('forum.hoursAgo')}`;
    if (dd < 7) return `${dd}${t('forum.daysAgo')}`;
    return formatDate(ds);
  };

  if (loading) return <div className="-mt-16"><section className="pt-32 pb-16"><div className="max-w-4xl mx-auto px-4 text-center text-gray-500">{t('postDetail.loading')}</div></section></div>;
  if (!post) return <div className="-mt-16"><section className="pt-32 pb-16"><div className="max-w-4xl mx-auto px-4 text-center"><h2 className="text-2xl font-bold text-white mb-4">{t('postDetail.notFound')}</h2><Link to="/forum" className="text-accent hover:text-accent-hover transition-colors">{t('postDetail.backToForum')}</Link></div></section></div>;

  const isOwner = currentUser && post.author_id === currentUser.id;
  const isAdmin = currentUser?.role === 'admin';
  const handleHidePost = async () => { if (!window.confirm(language === 'zh' ? '确定要隐藏此帖吗？' : 'Hide this post?')) return; try { await apiClient.hidePost(postId); setPost(p => ({ ...p, is_hidden: true })); } catch { alert('Failed'); } };
  const handleUnhidePost = async () => { try { await apiClient.unhidePost(postId); setPost(p => ({ ...p, is_hidden: false })); } catch { alert('Failed'); } };
  const authorName = post.author?.username || post.author?.name || t('postDetail.anonymous');

  return (
    <div className="-mt-16">
      <section className="relative pt-28 pb-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh opacity-50" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[400px] h-[400px] bg-gradient-to-br from-emerald-600/8 to-transparent rounded-full blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate('/forum')} className="mb-4 flex items-center text-gray-400 hover:text-white transition-colors text-sm">
            <ArrowLeft className="h-4 w-4 mr-2" />{t('postDetail.backToForum')}
          </button>
          <div className="flex items-center gap-2 mb-3">
            {post.is_pinned && <span className="px-2 py-0.5 bg-amber-500/10 text-amber-400 text-xs font-bold rounded-full border border-amber-500/20">{t('forum.pinned')}</span>}
            {post.is_hidden && <span className="px-2 py-0.5 bg-dark-surface text-gray-500 text-xs rounded-full border border-dark-border">{language === 'zh' ? '已隐藏' : 'Hidden'}</span>}
            <span className="px-2 py-0.5 bg-accent/10 text-accent text-xs rounded-full border border-accent/20">{post.category?.name || t('forum.uncategorized')}</span>
          </div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-white mb-3">{post.title}</h1>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center"><User className="h-4 w-4 mr-1" />{authorName}</span>
            <span className="flex items-center"><Clock className="h-4 w-4 mr-1" />{formatDate(post.created_at)}</span>
            <span>{t('postDetail.views')} {post.views || 0}</span>
            <span>{t('postDetail.replies')} {post.reply_count || 0}</span>
          </div>
        </div>
      </section>

      <section className="relative py-8 md:py-12 min-h-[50vh]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-dark-card rounded-2xl border border-dark-border shadow-dark-card p-6 md:p-8 mb-6">
            {(isOwner || isAdmin) && (
              <div className="flex gap-2 mb-4 justify-end">
                {isAdmin && (post.is_hidden
                  ? <button onClick={handleUnhidePost} className="p-2 text-gray-500 hover:text-emerald-400 transition-colors"><Eye className="h-5 w-5" /></button>
                  : <button onClick={handleHidePost} className="p-2 text-gray-500 hover:text-amber-400 transition-colors"><EyeOff className="h-5 w-5" /></button>
                )}
                {isOwner && <>
                  <button onClick={() => navigate(`/connections/post/${postId}/edit`)} className="p-2 text-gray-500 hover:text-white transition-colors"><Edit className="h-5 w-5" /></button>
                  <button onClick={handleDeletePost} className="p-2 text-gray-500 hover:text-red-400 transition-colors"><Trash2 className="h-5 w-5" /></button>
                </>}
              </div>
            )}
            <div className="prose max-w-none text-gray-300 whitespace-pre-wrap leading-relaxed">{post.content}</div>
          </div>

          <div className="bg-dark-card rounded-2xl border border-dark-border shadow-dark-card p-6 md:p-8">
            <h2 className="text-lg font-semibold text-white mb-6 flex items-center"><MessageSquare className="h-5 w-5 mr-2 text-accent" />{t('postDetail.replyTitle')} ({replies.length})</h2>
            {currentUser ? (
              <form onSubmit={handleSubmitReply} className="mb-8">
                <textarea value={replyContent} onChange={(e) => setReplyContent(e.target.value)} placeholder={t('postDetail.replyPlaceholder')} rows={4} required
                  className="w-full p-4 border border-dark-border rounded-xl bg-dark-surface text-white placeholder-gray-500 focus:ring-2 focus:ring-accent/30 focus:border-accent/50 outline-none resize-none transition-all" />
                <div className="mt-3 flex justify-end">
                  <button type="submit" disabled={submitting || !replyContent.trim()}
                    className="px-6 py-2 bg-white text-dark rounded-full font-semibold text-sm hover:shadow-glow-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
                    <Send className="h-4 w-4" />{submitting ? t('postDetail.submitting') : t('postDetail.submitReply')}
                  </button>
                </div>
              </form>
            ) : (
              <div className="mb-8 p-4 bg-dark-surface rounded-xl text-center text-sm text-gray-500 border border-dark-border">
                {language === 'zh' ? '请' : 'Please '}<Link to="/login" className="text-accent hover:text-accent-hover transition-colors">{t('nav.login')}</Link>{language === 'zh' ? '后回复' : ' to reply'}
              </div>
            )}
            <div className="space-y-6">
              {replies.length === 0 ? <div className="text-center text-gray-500 py-8 text-sm">{t('postDetail.noReplies')}</div>
                : replies.map((reply) => (
                  <div key={reply.id} className="border-b border-dark-border/50 pb-6 last:border-0 last:pb-0">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-dark-surface rounded-full flex items-center justify-center flex-shrink-0 border border-dark-border"><User className="h-5 w-5 text-gray-500" /></div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-medium text-white text-sm">{reply.author?.username || reply.author?.name || t('postDetail.anonymous')}</span>
                          <span className="text-xs text-gray-500">{formatRelativeTime(reply.created_at)}</span>
                        </div>
                        <div className="text-gray-400 whitespace-pre-wrap text-sm leading-relaxed">{reply.content}</div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PostDetail;
